/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import * as textConstants from '../../constants/StudioTextConstants';
import utils from '../../../../common/utils';

/**
 * DeleteContentService - Handles removing uploaded content
 * API: POST /api/v2.0/upload/content/:fileId
 */
export default class DeleteContentService extends BaseService {
  constructor() {
    super(constants.DELETE_CONTENT, 'deleteContentStatus');
  }

  getErrorMessage(data, fallback) {
    return data?.error || data?.message || fallback;
  }

  handleEvent(eventName, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);
    const { fileId } = payload;

    // Store fileId on the instance so handleResponse can use it as a fallback
    // if the API response does not echo the fileId back (same pattern as UploadContentService)
    this.fileId = fileId;

    return this.getClient('aiadminv2').post(`/upload/content/${fileId}`, {}, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    if (data && data.success) {
      const fileId = data.data?.fileId || this.fileId;
      const notification = {
        open: true,
        type: 'success',
        message: textConstants.DELETE_CONTENT_SUCCESS_MSG
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      Framework.getStoreRegistry().getStore('contentUploadModel').setDeleteSuccess(fileId);
      Framework.getEventManager().publish(constants.DELETE_CONTENT_SUCCESS, { data: data.data });
    } else {
      const msg = this.getErrorMessage(data, textConstants.DELETE_CONTENT_FAILED_MSG);
      const notification = {
        open: true,
        type: 'error',
        message: msg
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      Framework.getStoreRegistry().getStore('contentUploadModel').setDeleteError(msg);
      Framework.getEventManager().publish(constants.DELETE_CONTENT_ERROR);
    }
  }

  handleError(error) {
    const { response } = error;
    let msg = textConstants.DELETE_CONTENT_REQUEST_FAILED_MSG;

    if (response) {
      const { status, data } = response;
      if (status === 401) {
        utils.redirectToLogin();
        Framework.getEventManager().publish(constants.DELETE_CONTENT_ERROR);
        return;
      }
      msg = this.getErrorMessage(data, msg);
    } else if (error.message === `timeout of ${constants.BERLIN_API_TIMEOUT}ms exceeded`) {
      msg = 'Request timeout. Please try again.';
    }

    const notification = {
      open: true,
      message: msg,
      type: 'error'
    };
    Framework.getStoreRegistry().getStore('notificationState').set(notification);
    Framework.getStoreRegistry().getStore('contentUploadModel').setDeleteError(msg);
    Framework.getEventManager().publish(constants.DELETE_CONTENT_ERROR);
  }
}
